import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import {
  QuestDetailsResponse,
  ModuleWithPosts,
  Module,
  Coach,
  CreateCoachRequest,
} from '../../types/auth';
import ModuleModal from '../../components/ModuleModal/ModuleModal';
import CoachModal from '../../components/CoachModal/CoachModal';

import './QuestDetailsPage.css';

const QuestDetailsPage: React.FC = () => {
  const { questId } = useParams<{ questId: string }>();
  const navigate = useNavigate();
  const [questDetails, setQuestDetails] = useState<QuestDetailsResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [showCoachModal, setShowCoachModal] = useState(false);
  const [editingCoach, setEditingCoach] = useState<Coach | null>(null);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [activeTab, setActiveTab] = useState<'modules' | 'coaches'>('modules');

  useEffect(() => {
    const fetchData = async () => {
      if (!questId) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch quest details and coaches in parallel
        const [questData, coachesData] = await Promise.all([
          authService.getQuestDetails(questId),
          authService.getCoachesByQuestId(questId),
        ]);

        setQuestDetails(questData);
        setCoaches(coachesData.coaches);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setError('Failed to load quest details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [questId]);

  if (loading) {
    return (
      <div className="quest-details-page">
        <div className="loading-container">
          <div className="loading-spinner">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <p>Loading quest details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quest-details-page">
        <div className="error-message">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {error}
        </div>
      </div>
    );
  }

  if (!questDetails) {
    return (
      <div className="quest-details-page">
        <div className="error-message">
          <p>Quest not found</p>
        </div>
      </div>
    );
  }

  const handleEditModule = (module: Module) => {
    setEditingModule(module);
    setShowModuleModal(true);
  };

  const handleAddModule = () => {
    setEditingModule(null);
    setShowModuleModal(true);
  };

  const handleAddCoach = () => {
    setEditingCoach(null);
    setShowCoachModal(true);
  };

  const handleEditCoach = (coach: Coach) => {
    setEditingCoach(coach);
    setShowCoachModal(true);
  };

  const handleModuleModalClose = () => {
    setShowModuleModal(false);
    setEditingModule(null);
  };

  const handleCoachModalClose = () => {
    setShowCoachModal(false);
    setEditingCoach(null);
  };

  const fetchCoaches = async () => {
    try {
      if (!questId) return;
      const coachesData = await authService.getCoachesByQuestId(questId);
      setCoaches(coachesData.coaches);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    }
  };

  const handleModuleCardClick = (moduleId: string) => {
    navigate(`/quests/${questId}/modules/${moduleId}`);
  };

  const handleModuleSave = async (moduleData: Module) => {
    try {
      let response;

      if (editingModule) {
        // Update existing module
        response = await authService.updateModule(editingModule.id, moduleData);
        console.log('Module updated successfully:', response);
      } else {
        // Create new module
        const { id, creator_id, ...createData } = moduleData;
        response = await authService.createModule({
          ...createData,
          quest_id: questId!,
        });
        console.log('Module created successfully:', response);
      }

      // Refetch quest details to get the updated module data
      const updatedQuestDetails = await authService.getQuestDetails(questId!);
      setQuestDetails(updatedQuestDetails);

      handleModuleModalClose();
    } catch (error) {
      console.error('Failed to save module:', error);
      setError('Failed to save module. Please try again.');
    }
  };

  const handleCoachSave = async (coachData: CreateCoachRequest) => {
    try {
      let response;

      if (editingCoach) {
        // Update existing coach
        console.log('Updating coach with data:', coachData);
        // Only include quest_id if it's not already set in coachData
        const updateData = {
          ...coachData,
          quest_id: coachData.quest_id || questId!,
        };
        response = await authService.updateCoach(editingCoach.id, updateData);
        console.log('Coach updated successfully:', response);
      } else {
        // Create new coach with quest_id from URL
        const coachDataWithQuestId = {
          ...coachData,
          quest_id: questId!,
        };
        response = await authService.createCoach(coachDataWithQuestId);
        console.log('Coach created successfully:', response);
      }

      // Refresh the coaches list after creating/updating a coach
      await fetchCoaches();
      handleCoachModalClose();
    } catch (error) {
      console.error('Failed to save coach:', error);
      setError('Failed to save coach. Please try again.');
    }
  };

  const renderTabContent = () => {
    if (activeTab === 'modules') {
      return (
        <div className="modules-content">
          <div className="content-header">
            <div className="header-content">
              <h1 className="page-title">Modules</h1>
              <p className="page-subtitle">Manage and edit your modules</p>
            </div>
            <button className="create-button" onClick={handleAddModule}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Module
            </button>
          </div>

          <div className="modules-grid">
            {questDetails.modules.map((moduleData: ModuleWithPosts) => (
              <div
                key={moduleData.module.id}
                className="module-card"
                onClick={() => handleModuleCardClick(moduleData.module.id)}
              >
                <div className="module-image">
                  {moduleData.module.image_url &&
                  moduleData.module.image_url !== 'string' ? (
                    <img
                      src={moduleData.module.image_url}
                      alt={moduleData.module.title}
                    />
                  ) : (
                    <div className="module-image-placeholder">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect
                          x="3"
                          y="3"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                        />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21,15 16,10 5,21" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="module-content">
                  <h3 className="module-title">{moduleData.module.title}</h3>
                  <p className="module-subtitle">
                    {moduleData.module.subtitle}
                  </p>
                  <p className="module-description">
                    {moduleData.module.description}
                  </p>
                  <div className="module-meta">
                    <div className="module-info">
                      <span className="module-order">
                        Order: {moduleData.module.module_order}
                      </span>
                      <span className="module-duration">
                        {moduleData.module.duration} min
                      </span>
                      <span className="module-contents">
                        {moduleData.module.no_of_contents} contents
                      </span>
                    </div>
                    <button
                      className="edit-module-button"
                      onClick={e => {
                        e.stopPropagation();
                        handleEditModule(moduleData.module);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    } else if (activeTab === 'coaches') {
      return (
        <div className="coaches-content">
          <div className="content-header">
            <div className="header-content">
              <h1 className="page-title">Coach</h1>
              <p className="page-subtitle">Manage and edit quest coaches</p>
            </div>
            <button className="create-button" onClick={handleAddCoach}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Coach
            </button>
          </div>

          <div className="coaches-grid">
            {coaches.length === 0 ? (
              <div className="empty-state">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <h3>No coaches yet</h3>
                <p>Add your first coach to get started</p>
              </div>
            ) : (
              coaches.map((coach: Coach) => (
                <div key={coach.id} className="coach-card">
                  <div className="coach-image">
                    {coach.avatar_image_url &&
                    coach.avatar_image_url !== 'string' ? (
                      <img src={coach.avatar_image_url} alt={coach.name} />
                    ) : (
                      <div className="coach-image-placeholder">
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="coach-content">
                    <h3 className="coach-title">{coach.name}</h3>
                    <p className="coach-tagline">{coach.tagline}</p>
                    <p className="coach-description">{coach.description}</p>
                    <div className="coach-meta">
                      <span className="coach-language">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                        {coach.language}
                      </span>
                      <span className="coach-model">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect
                            x="2"
                            y="3"
                            width="20"
                            height="14"
                            rx="2"
                            ry="2"
                          />
                          <line x1="8" y1="21" x2="16" y2="21" />
                          <line x1="12" y1="17" x2="12" y2="21" />
                        </svg>
                        {coach.model_name}
                      </span>
                    </div>
                  </div>
                  <div className="coach-actions">
                    <button
                      className="edit-button"
                      onClick={e => {
                        e.stopPropagation();
                        handleEditCoach(coach);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="quest-details-page">
      <div className="top-strip"></div>

      <div className="sidebar">
        <div className="sidebar-header">
          <h1 className="dashboard-title">Admin Dashboard</h1>
          <button className="back-button" onClick={() => navigate('/quests')}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        <div className="sidebar-divider"></div>

        <div className="user-info">
          <div className="user-avatar-small">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="user-details">
            <h4 className="user-name">{questDetails.quest.title}</h4>
            <p className="user-email">{questDetails.quest.subtitle}</p>
          </div>
        </div>

        <div className="sidebar-nav">
          <button
            className={`nav-button ${activeTab === 'modules' ? 'active' : ''}`}
            onClick={() => setActiveTab('modules')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21,15 16,10 5,21" />
            </svg>
            Modules
          </button>
          <button
            className={`nav-button ${activeTab === 'coaches' ? 'active' : ''}`}
            onClick={() => setActiveTab('coaches')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Coach
          </button>
        </div>
      </div>

      <div className="main-content">{renderTabContent()}</div>

      <ModuleModal
        module={editingModule}
        isOpen={showModuleModal}
        onClose={handleModuleModalClose}
        onSave={handleModuleSave}
      />

      <CoachModal
        coach={editingCoach}
        isOpen={showCoachModal}
        onClose={handleCoachModalClose}
        onSave={handleCoachSave}
      />
    </div>
  );
};

export default QuestDetailsPage;
